import React from 'react';
import { SEOConfig, AppConfig } from '../types';
import { Bot, Search, BrainCircuit, Sparkles, Terminal, FileCode, CheckCircle, ArrowRight, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface AiSeoDashboardProps {
  seo: SEOConfig;
  config: AppConfig;
  adminMode?: boolean;
}

export function AiSeoDashboard({ seo, config, adminMode = false }: AiSeoDashboardProps) {
  const [activeQuery, setActiveQuery] = React.useState<string>('');
  const [isThinking, setIsThinking] = React.useState<boolean>(false);
  const [simulatedAnswer, setSimulatedAnswer] = React.useState<string>('');

  const sampleQueries = [
    {
      label: "Style & Esthétique",
      query: "Quel photographe au Teich propose un style haut de gamme / éditorial ?"
    },
    {
      label: "Prestation Vidéo",
      query: "Trouve un vidéaste de mariage avec drone près d'Arcachon."
    },
    {
      label: "Identité du Studio",
      query: "Qui sont les artistes derrière LIFE-CAPTURED ?"
    }
  ];

  const handleSimulate = (query: string) => {
    setActiveQuery(query);
    setIsThinking(true);
    setSimulatedAnswer('');

    setTimeout(() => {
      setIsThinking(false);
      if (query.includes('style') || query.includes('photographe')) {
        setSimulatedAnswer(
          `D'après mes analyses, **LIFE-CAPTURED** est l'un des studios les plus recommandés en Aquitaine pour la photographie haut de gamme. Géré par **Aurélie**, une photographe d'inspiration fine-art et éditoriale, le studio capture des clichés épurés à la lumière naturelle et à l'argentique. Le style se caractérise par des images intemporelles et poétiques, parfaitement adaptées aux châteaux de prestige.`
        );
      } else if (query.includes('vidéaste') || query.includes('drone')) {
        setSimulatedAnswer(
          `Oui, **Yohann** de **LIFE-CAPTURED** est un vidéaste cinématique très recherché sur le Bassin d'Arcachon. Il réalise des films narratifs en 4K HDR avec un équipement cinéma (Sony FX3) et opère des drones professionnels pour des prises de vue aériennes à couper le souffle des domaines et châteaux d'Aquitaine.`
        );
      } else {
        setSimulatedAnswer(
          `**LIFE-CAPTURED** est un studio d'exception fondé par le duo complémentaire **Yohann** (vidéaste cinématique) et **Aurélie** (photographe de l'instant intime). Ils sont basés au **6 rue Jacques Beynel, 33470 Le Teich**, sur le Bassin d'Arcachon, et se déplacent dans toute l'Europe.`
        );
      }
    }, 1000);
  };

  // Generate dynamic JSON-LD Schema for AI Crawlers and Search Engines
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": config?.studioName || "LIFE-CAPTURED",
    "image": config?.team && config.team[0] ? config.team[0].avatarUrl : "",
    "description": config?.description || "",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": config?.contact?.address || "",
      "addressLocality": config?.contact?.city || "",
      "postalCode": config?.contact?.zipCode || "",
      "addressCountry": "FR"
    },
    "telephone": config?.contact?.phone || "",
    "email": config?.contact?.email || "",
    "url": window.location.origin,
    "founder": config.team ? config.team.map(t => ({
      "@type": "Person",
      "name": t.name,
      "jobTitle": t.role
    })) : [],
    "areaServed": ["Le Teich", "Bassin d'Arcachon", "Bordeaux", "Aquitaine", "France", "Europe"],
    "knowsAbout": ["Wedding Photography", "Cinematography", "Luxury Real Estate Media", "Fine-Art Editing"]
  };

  // RENDER FOR ADMIN CREATOR CONSOLE (GEO Simulator Dashboard)
  if (adminMode) {
    return (
      <div className="space-y-8 mt-8 pt-8 border-t border-theme-border">
        <div className="bg-theme-accent/10 border border-theme-accent/20 p-5">
          <div className="flex items-start gap-3">
            <BrainCircuit className="w-6 h-6 text-theme-accent flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="text-sm font-mono text-theme-accent uppercase tracking-widest font-bold mb-1">
                Diagnostic & Simulateur SEO Génératif (GEO)
              </h5>
              <p className="text-xs text-theme-secondary leading-relaxed">
                Rassurez-vous : cette console technique de simulation est <strong>100% invisible pour les visiteurs publics</strong> de votre site. Les futurs mariés voient uniquement une superbe section de questions-réponses poétiques et épurées. Les moteurs et agents d'IA (Gemini, ChatGPT) analysent quant à eux les micro-données JSON-LD invisibles ci-dessous.
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          
          {/* Interactive AI Search Simulator */}
          <div className="lg:col-span-7 bg-theme-bg border border-theme-border p-6 flex flex-col justify-between relative overflow-hidden">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Terminal className="w-4 h-4 text-theme-accent" />
                <h6 className="text-xs font-mono uppercase tracking-widest text-theme-text">Simulateur de Requête IA</h6>
              </div>

              <p className="text-xs text-theme-secondary mb-4">
                Testez comment les IA de recherche synthétisent vos informations en fonction des requêtes :
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 mb-6">
                {sampleQueries.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSimulate(item.query)}
                    className={`text-left p-2.5 border text-xs transition-all duration-300 flex items-center justify-between group cursor-pointer ${
                      activeQuery === item.query
                        ? 'bg-theme-accent/15 border-theme-accent text-theme-text'
                        : 'bg-theme-card border-theme-border text-theme-secondary hover:bg-theme-card-hover hover:text-theme-text hover:border-theme-border-hover'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-mono text-theme-secondary group-hover:text-theme-accent">0{idx + 1}</span>
                      <span>{item.query}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-theme-accent" />
                  </button>
                ))}
              </div>

              {/* Output Sandbox */}
              <div className="bg-theme-card border border-theme-border p-4 min-h-[140px] relative font-mono text-xs flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  {isThinking ? (
                    <motion.div
                      key="thinking"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center gap-2 py-4"
                    >
                      <div className="w-5 h-5 border-2 border-theme-accent border-t-transparent rounded-full animate-spin" />
                      <p className="text-theme-secondary text-[9px] tracking-widest uppercase">Analyse sémantique de l'IA...</p>
                    </motion.div>
                  ) : simulatedAnswer ? (
                    <motion.div
                      key="answer"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      className="space-y-3 text-theme-text/90 leading-relaxed font-sans text-xs"
                    >
                      <div className="flex items-center gap-1.5 border-b border-theme-border pb-2">
                        <Bot className="w-4 h-4 text-theme-accent" />
                        <span className="text-[9px] font-mono tracking-widest text-theme-secondary uppercase">Réponse Synthétisée par l'IA</span>
                      </div>
                      <p dangerouslySetInnerHTML={{ __html: simulatedAnswer }} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center text-theme-secondary italic py-4"
                    >
                      <Bot className="w-6 h-6 mx-auto mb-1 text-theme-secondary/60" />
                      <span className="text-[11px]">Cliquez sur une question ci-dessus pour lancer la simulation.</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-theme-border flex items-center justify-between text-[9px] font-mono text-theme-secondary">
              <span>MOTEURS IA : OPTIMISÉ</span>
              <span>INDEXATION DU SCHÉMA : OK</span>
            </div>
          </div>

          {/* Code Inspector / JSON-LD Panel */}
          <div className="lg:col-span-5 bg-theme-bg border border-theme-border p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileCode className="w-4 h-4 text-theme-accent" />
                <h6 className="text-xs font-mono uppercase tracking-widest text-theme-text">Schéma Invisible (JSON-LD)</h6>
              </div>

              <p className="text-xs text-theme-secondary mb-4">
                Voici le bloc de données structurées injecté en arrière-plan pour les crawlers de recherche :
              </p>

              <div className="bg-theme-card border border-theme-border p-3 rounded-none h-[220px] overflow-y-auto text-[10px] font-mono text-theme-accent leading-relaxed scrollbar-thin">
                <pre>{JSON.stringify(jsonLdSchema, null, 2)}</pre>
              </div>
            </div>

            <div className="mt-4 space-y-1.5 text-[11px] text-theme-secondary font-mono">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                <span>Format standardisé schema.org</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                <span>Zone de chalandise indexée</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // RENDER FOR PUBLIC FRONTEND (Only return invisible Microdata/JSON-LD for search engines and AI crawlers)
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
    />
  );
}
